import { Helmet } from 'react-helmet';
import { withNamespaces } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { handleApiErrors } from '../../api/axiosInstance';
import PromptsAPI from '../../api/prompts';
import { ConfirmModal } from '../../components';
import PromptsTable from '../../components/Table/PromptsTable';
import Toast from '../../components/Toast';
import PageContainer from '../../containers/PageContainer';
import { INTERNAL_LINKS } from '../../enum';
import withAuthentication from '../../hocs/withAuthentication';
import useModal from '../../hooks/useModal';
import { formatPageTitle } from '../../utils/common';

const PromptsView = props => {
  const { t, history } = props;
  const queryClient = useQueryClient();

  const [isDeleteModalVisible, openDeleteModal, closeDeleteModal, deleteModalProps] = useModal();

  const promptsQuery = useQuery({
    queryKey: ['fetchPrompts'],
    queryFn: PromptsAPI.fetchPrompts,
    placeholderData: {
      documents: [],
      totalCount: 0,
    },
    onError: error => handleApiErrors(error.response),
  });

  const deletePromptMutation = useMutation(promptId => PromptsAPI.deletePrompt(promptId), {
    onSuccess: () => {
      closeDeleteModal();
      queryClient.invalidateQueries(['fetchPrompts'], { exact: false });
      Toast({
        type: 'success',
        message: t('deletePromptSuccess'),
      });
    },
    onError: error => {
      handleApiErrors(error.response, () => {
        Toast({
          type: 'error',
          message: t('deletePromptError'),
        });
      });
    },
  });

  const handleDeleteClick = promptId => {
    openDeleteModal({
      promptId,
      title: t('deletePrompt'),
      message: t('areYouSureYouWantToDeleteThisPrompt?'),
      okText: t('yes'),
      cancelText: t('no'),
    });
  };

  return (
    <PageContainer
      title={t('prompts')}
      pageActionText={t('create')}
      onPageActionClick={() => history.push(INTERNAL_LINKS.CREATE_PROMPT)}
    >
      <Helmet>
        <title>{formatPageTitle('Prompts')}</title>
      </Helmet>

      <PromptsTable
        t={t}
        dataSource={promptsQuery.data?.documents || []}
        loading={promptsQuery.isFetching || deletePromptMutation.isLoading}
        onViewPromptClick={promptId => history.push(INTERNAL_LINKS.PROMPT_DETAILS(promptId))}
        onDeletePromptClick={handleDeleteClick}
      />

      <ConfirmModal
        visible={isDeleteModalVisible}
        title={deleteModalProps.title}
        message={deleteModalProps.message}
        okText={deleteModalProps.okText}
        cancelText={deleteModalProps.cancelText}
        loading={deletePromptMutation.isLoading}
        closable={!deletePromptMutation.isLoading}
        onCancel={closeDeleteModal}
        onOk={() => deletePromptMutation.mutateAsync(deleteModalProps.promptId)}
      />
    </PageContainer>
  );
};

export default withNamespaces()(withAuthentication(PromptsView));
